/**
 * Region / biome encounter generation. Deterministic daily routes from
 * `hash(worldSeed + inGameDate + nodeId)` (D6/D9): the same inputs always yield
 * the same route, which anchors the once-a-day cadence and keeps the world
 * reproducible. The renderer consumes the pre-rolled encounters; it owns no rules.
 *
 * "Gentle pity" (D6): on long droughts without a rare/very-rare species, rare-tier
 * odds are nudged upward — soft-capped so rarity stays rewarding, never explosive
 * or soul-crushing — and reset on a hit. Pure counters, no timers (CLAUDE.md: no
 * decay/FOMO). Optional team-strength fairness only ever *softens* a level toward
 * the team and never drops it below the slot's documented floor; the real
 * rubber-banding lives in difficulty.ts.
 *
 * Pure + deterministic: ALL randomness flows through an injected Rng (core/rng).
 */
import type {
  GenerateRouteFn,
  EncounterTable,
  EncounterEntry,
  EncounterSlot,
  GeneratedEncounter,
  GeneratedRoute,
  PityState,
} from '@/shared';
import type { Rng } from './rng.js';
import { makeRng, hash } from './rng.js';

// ── Tuning constants (gentle, decompression-first) ───────────────────────────

/** Rarity buckets by a slot-relative weight share. */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'very-rare';

/** Upper bound (inclusive) of the weight *share* for each rarity tier. */
const RARITY_THRESHOLDS: ReadonlyArray<{ tier: Rarity; maxShare: number }> = [
  { tier: 'very-rare', maxShare: 0.05 },
  { tier: 'rare', maxShare: 0.15 },
  { tier: 'uncommon', maxShare: 0.4 },
  { tier: 'common', maxShare: 1.0 },
];

/** Approximate base shiny rate (the classic ~1/4096), gently floored for joy. */
const BASE_SHINY_RATE = 1 / 4096;
/** Per-drought-step shiny lift; soft-capped via SHINY_MAX_MULT. */
const SHINY_PITY_PER_STEP = 0.02;
const SHINY_MAX_MULT = 8;

/** Per-drought-step rare-tier weight lift and its gentle ceiling. */
const RARE_PITY_PER_STEP = 0.03;
const RARE_MAX_MULT = 4;

/** Route length band (steps the expedition walks). Short = closes cleanly. */
const ROUTE_MIN_STEPS = 8;
const ROUTE_MAX_STEPS = 16;

/**
 * Fraction of the team/encounter level gap closed when team strength is given.
 * Small + one-directional-ish: only softens spikes, never grinds, never drops a
 * level below its slot floor. The authoritative rubber-band lives in difficulty.ts.
 */
const FAIRNESS_PULL = 0.25;

// ── Pity helpers ─────────────────────────────────────────────────────────────

/** A neutral, drought-free pity state (new game / after a generous run). */
export function emptyPity(): PityState {
  return { stepsSinceRare: 0, stepsSinceShiny: 0 };
}

/** Bucket a single slot entry by its share of the slot's total weight. */
export function classifyRarity(weight: number, slotTotalWeight: number): Rarity {
  const share = slotTotalWeight > 0 ? weight / slotTotalWeight : 1;
  for (const { tier, maxShare } of RARITY_THRESHOLDS) {
    if (share <= maxShare) return tier;
  }
  return 'common';
}

/** Whether a rarity tier is one the pity system is allowed to lift. */
function isRareTier(r: Rarity): boolean {
  return r === 'rare' || r === 'very-rare';
}

/**
 * The gentle multiplier applied to a rare-tier entry's weight given the current
 * drought. Neutral (1.0) for common/uncommon and at zero drought; rises with the
 * rare drought and is soft-capped so it can never become explosive.
 */
export function rarityWeightMultiplier(rarity: Rarity, pity: PityState): number {
  if (!isRareTier(rarity)) return 1;
  const lift = 1 + RARE_PITY_PER_STEP * Math.max(0, pity.stepsSinceRare);
  return Math.min(RARE_MAX_MULT, lift);
}

/** The gentle, soft-capped shiny rate given the current shiny drought. */
function shinyRate(pity: PityState): number {
  const mult = Math.min(
    SHINY_MAX_MULT,
    1 + SHINY_PITY_PER_STEP * Math.max(0, pity.stepsSinceShiny),
  );
  return Math.min(1, BASE_SHINY_RATE * mult);
}

/**
 * Fold one encounter into the pity state (pure). Long droughts climb; a rare-tier
 * find resets the rare drought, a shiny resets the shiny drought. Replayable:
 * `updatePity` of the same (state, encounter, rarity) always yields the same next
 * state, so a route can be re-simulated deterministically.
 */
export function updatePity(
  pity: PityState,
  encounter: GeneratedEncounter,
  rarity: Rarity,
): PityState {
  const stepsSinceRare = isRareTier(rarity) ? 0 : pity.stepsSinceRare + 1;
  const stepsSinceShiny = encounter.shiny ? 0 : pity.stepsSinceShiny + 1;
  return { stepsSinceRare, stepsSinceShiny };
}

// ── Weighted slot selection ──────────────────────────────────────────────────

/** Partition a table's entries by slot, preserving order. */
function entriesBySlot(table: EncounterTable): Map<EncounterSlot, EncounterEntry[]> {
  const out = new Map<EncounterSlot, EncounterEntry[]>();
  for (const e of table.entries) {
    const bucket = out.get(e.slot);
    if (bucket) bucket.push(e);
    else out.set(e.slot, [e]);
  }
  return out;
}

interface WeightedEntry {
  readonly entry: EncounterEntry;
  readonly rarity: Rarity;
  /** Effective (pity-lifted) weight used for selection. */
  readonly weight: number;
}

/** Precompute rarity + pity-adjusted weights for one slot's entries. */
function prepareSlot(entries: readonly EncounterEntry[], pity: PityState): WeightedEntry[] {
  const total = entries.reduce((s, e) => s + Math.max(0, e.weight), 0);
  return entries.map((entry) => {
    const rarity = classifyRarity(entry.weight, total);
    const weight = Math.max(0, entry.weight) * rarityWeightMultiplier(rarity, pity);
    return { entry, rarity, weight };
  });
}

/** Pick one weighted entry from a prepared slot using the injected Rng. */
function pickWeighted(prepared: readonly WeightedEntry[], rng: Rng): WeightedEntry {
  const total = prepared.reduce((s, w) => s + w.weight, 0);
  if (total <= 0) return prepared[0]!;
  let roll = rng.float() * total;
  for (const w of prepared) {
    roll -= w.weight;
    if (roll < 0) return w;
  }
  return prepared[prepared.length - 1]!;
}

/**
 * Roll a level within an entry's band. Optional team strength gently pulls the
 * level toward the team without ever dropping below the band floor or rising past
 * the band ceiling (decompression: encounters never become unfairly hard/easy).
 */
function rollLevel(
  band: EncounterEntry['levelBand'],
  rng: Rng,
  teamAverageLevel: number | undefined,
): number {
  const span = Math.max(0, band.max - band.min);
  let level = band.min + rng.int(span + 1);
  if (teamAverageLevel !== undefined && Number.isFinite(teamAverageLevel)) {
    const pulled = level + (teamAverageLevel - level) * FAIRNESS_PULL;
    level = Math.round(pulled);
  }
  return Math.min(band.max, Math.max(band.min, level));
}

// ── Public generation ────────────────────────────────────────────────────────

/** Arguments accepted by {@link generateRoute} (the frozen set + optional fairness). */
export type GenerateRouteArgs = Parameters<GenerateRouteFn>[0] & {
  /**
   * Optional average team level. When absent the generator stays neutral (levels
   * are drawn strictly within the slot bands). When present it *gently* nudges
   * encounter levels toward the team — softening spikes only.
   */
  readonly teamAverageLevel?: number;
};

/**
 * Generate the day's route + its pre-rolled, visible wild encounters.
 * Pure given its inputs; seeded by `hash(worldSeed + inGameDate + nodeId)`.
 */
function generateRouteImpl(args: GenerateRouteArgs): GeneratedRoute {
  const { worldSeed, inGameDate, node, biome, pity, teamAverageLevel } = args;

  const seed = hash(worldSeed, inGameDate, node.id);
  const rng = makeRng(seed);

  const slots = entriesBySlot(biome.encounters);
  // Deterministic, stable slot order so generation never depends on Map insertion
  // quirks across engines.
  const slotOrder: EncounterSlot[] = ['grass', 'surf', 'fishing', 'cave', 'static'].filter((s) =>
    slots.has(s as EncounterSlot),
  ) as EncounterSlot[];

  // Pre-prepare each slot once; pity is the route's *entry* state (carried in from
  // the save), so the lift is stable for the whole route — simple + replayable.
  const prepared = new Map<EncounterSlot, WeightedEntry[]>();
  for (const slot of slotOrder) {
    prepared.set(slot, prepareSlot(slots.get(slot)!, pity));
  }

  const length = ROUTE_MIN_STEPS + rng.int(ROUTE_MAX_STEPS - ROUTE_MIN_STEPS + 1);

  // Several visible encounters per route (roughly a third of the steps), clamped
  // to leave quiet walking between them.
  const desired = Math.max(2, Math.round(length * 0.35));

  const encounters: GeneratedEncounter[] = [];
  // Walk the pity forward across the route so successive draws within one day
  // also feel the gentle lift; deterministic because rng + updatePity are pure.
  let runningPity: PityState = { ...pity };

  for (let i = 0; i < desired; i++) {
    const slot = slotOrder[rng.int(slotOrder.length)]!;
    const slotPrepared = prepared.get(slot)!;
    // Re-prepare lazily only if the running pity has lifted further than entry.
    const live =
      runningPity.stepsSinceRare === pity.stepsSinceRare
        ? slotPrepared
        : prepareSlot(slots.get(slot)!, runningPity);

    const chosen = pickWeighted(live, rng);
    const level = rollLevel(chosen.entry.levelBand, rng, teamAverageLevel);
    const shiny = rng.float() < shinyRate(runningPity);

    const encounter: GeneratedEncounter = {
      speciesId: chosen.entry.speciesId,
      level,
      slot,
      shiny,
    };
    encounters.push(encounter);
    runningPity = updatePity(runningPity, encounter, chosen.rarity);
  }

  return {
    nodeId: node.id,
    biomeId: biome.id,
    date: inGameDate,
    length,
    encounters,
  };
}

// Compile-time guard: the impl must satisfy the frozen contract (it accepts the
// frozen arg set plus one optional field, so it remains assignable to the type).
const _contractCheck: GenerateRouteFn = generateRouteImpl;
void _contractCheck;

/** Public entry point. Conforms to the frozen {@link GenerateRouteFn}. */
export const generateRoute = generateRouteImpl;
